// Ejemplos: alimentación de la placa (batería en VIN, pilas en serie y reinicios por bajo voltaje).
import { at, part, wires, UNO } from './helpers.js';

const UNO_BAT = { ...UNO, props: { usb: false } };
const BB_TOP = { id: 'bb1', type: 'breadboard', x: 0, z: -80, rot: 0, props: { size: 'half' } };
const BB = { id: 'bb1', type: 'breadboard', x: 0, z: 80, rot: 0, props: { size: 'half' } };
const BB_RIGHT = { id: 'bb1', type: 'breadboard', x: 52, z: 88, rot: 0, props: { size: 'half' } };

export const EXAMPLES = [
  {
    id: 'bateria-vin',
    category: 'Alimentación',
    name: 'Arduino con batería de 9 V (sin USB)',
    description: 'Sin el cable USB, una batería de 9 V entre VIN y GND alimenta la placa: el regulador da los 5 V. Sin USB el monitor serie no recibe nada. Haz clic en el conector USB para enchufarlo o desenchufarlo.',
    project: {
      name: 'Batería en VIN',
      files: {
        'sketch.ino': `// Arduino alimentado con una batería de 9 V en VIN (el cable USB está desconectado).
// El regulador de la placa (NCP1117) baja los 7–12 V de VIN a 5 V. La batería tiene resistencia
// interna (~1.7 Ω): con la placa y el LED (~60 mA) su voltaje baja un poco, a ~8.9 V.
// Sin cable USB no hay computadora: lo que mande Serial no llega a ningún lado.

const int LED = 13;

void setup() {
  pinMode(LED, OUTPUT);
}

void loop() {
  digitalWrite(LED, HIGH);
  delay(500);
  digitalWrite(LED, LOW);
  delay(500);
}
`,
      },
      parts: [
        UNO_BAT,
        BB_TOP,
        part('led1', 'led', { color: 'green' }, at(BB_TOP, 'f20', 'led')),
        part('r1', 'resistor', { value: 220 }, at(BB_TOP, 'h21', 'resistor')),
        part('bat', 'battery', { voltage: '9' }, { x: -66, z: 64, rot: 0 }),
      ],
      wires: wires([
        ['uno:13', 'bb1:j25', 'green'],
        ['bb1:j20', 'bb1:bn16', 'black'],
        ['bb1:bn11', 'uno:GND.1', 'black'],
        ['bat:+', 'uno:VIN', 'red'],
        ['bat:-', 'uno:GND.2', 'black'],
      ]),
    },
  },
  {
    id: 'pilas-serie',
    category: 'Alimentación',
    name: 'Dos pilas 18650 en serie (7.4 V)',
    description: 'Dos pilas de 3.7 V en serie dan 7.4 V en VIN. Con más de 6.6 V en VIN la placa deja de tomar corriente del USB, pero el monitor serie sigue funcionando: un divisor en A0 mide el voltaje de las pilas.',
    project: {
      name: 'Pilas en serie',
      files: {
        'sketch.ino': `// Dos pilas 18650 en serie: el + de la primera va al − de la segunda, así sus voltajes se suman
// (3.7 V + 3.7 V = 7.4 V). El − de la primera va a GND y el + de la segunda a VIN.
// Con más de 6.6 V en VIN, un comparador de la placa desconecta el USB de la alimentación: la placa
// vive de las pilas, pero el cable USB sigue llevando los datos al monitor serie.
// El divisor de 10 kΩ + 10 kΩ lleva la mitad del voltaje de las pilas a A0 (el ADC mide hasta 5 V).

void setup() {
  Serial.begin(9600);
}

void loop() {
  int lectura = analogRead(A0);
  float pilas = lectura * (5.0 / 1023.0) * 2;   // el divisor divide entre 2
  Serial.print("Pilas en serie: ");
  Serial.print(pilas, 2);
  Serial.println(" V");
  delay(500);
}
`,
      },
      parts: [
        UNO,
        BB_RIGHT,
        part('b1', 'battery', { voltage: '3.7' }, { x: -96, z: 52, rot: 0 }),
        part('b2', 'battery', { voltage: '3.7' }, { x: -96, z: 84, rot: 0 }),
        part('r1', 'resistor', { value: 10000 }, at(BB_RIGHT, 'c5', 'resistor')),
        part('r2', 'resistor', { value: 10000 }, at(BB_RIGHT, 'e9', 'resistor')),
      ],
      wires: wires([
        ['b1:-', 'uno:GND.2', 'black'],
        ['b1:+', 'b2:-', 'orange'],
        ['b2:+', 'uno:VIN', 'red'],
        ['b2:+', 'bb1:a5', 'red'],
        ['bb1:a9', 'uno:A0', 'yellow'],
        ['bb1:a13', 'uno:GND.3', 'black'],
      ]),
    },
  },
  {
    id: 'motores-reinicio',
    category: 'Alimentación',
    name: 'Motores en el 5V: el Arduino se reinicia',
    description: 'Tres motores en el pin 5V con la placa en una batería de 9 V: al arrancar piden ~4 A, el regulador da ~1.5 A, el 5V cae a ~2 V y el ATmega se reinicia una y otra vez (brown-out). El LED L parpadea 3 veces en cada arranque.',
    warnings: { must: ['brownout'], may: ['reg-current'] },
    project: {
      name: 'Motores y reinicios',
      files: {
        'sketch.ino': `// Tres motores de CD conectados al pin 5V del Arduino, que vive de una batería de 9 V en VIN.
// Al arrancar, con el rotor parado, cada motor pide ~1.3 A. El regulador de la placa (NCP1117) da
// como mucho ~1.5 A: el 5V cae a ~2 V y el ATmega328P se reinicia por bajo voltaje (brown-out en 2.7 V).
// En el reinicio el pin 9 queda suelto, la resistencia de 10 kΩ apaga el MOSFET y los motores se
// detienen (su corriente sigue un momento por su diodo); el 5V vuelve, el programa arranca otra vez...
// y se repite para siempre.
// Con uno o dos motores sí alcanza (el regulador solo se calienta). La solución: alimenta los motores
// directo de la batería (no del pin 5V) y une su − con el GND del Arduino.

const int MOTORES = 9;   // compuerta del MOSFET IRLZ44N

void setup() {
  pinMode(LED_BUILTIN, OUTPUT);
  for (int i = 0; i < 3; i++) {   // 3 parpadeos: el programa acaba de arrancar
    digitalWrite(LED_BUILTIN, HIGH);
    delay(100);
    digitalWrite(LED_BUILTIN, LOW);
    delay(100);
  }
  pinMode(MOTORES, OUTPUT);
  digitalWrite(MOTORES, HIGH);    // los tres motores a la vez
}

void loop() {
}
`,
      },
      parts: [
        UNO_BAT,
        BB,
        part('q1', 'mosfet-n', {}, at(BB, 'f10', 'mosfet-n')),
        part('rg', 'resistor', { value: 220 }, at(BB, 'g6', 'resistor')),
        part('rpd', 'resistor', { value: 10000 }, at(BB, 'bn8', 'resistor', { rot: 90, pin: '1' })),
        // Un diodo 1N4007 por motor (franja hacia el +): la columna del drenador sigue en la 16 y la 21.
        part('d1', 'diode', {}, at(BB, 'i11', 'diode', { rot: 270 })),
        part('d2', 'diode', {}, at(BB, 'i16', 'diode', { rot: 270 })),
        part('d3', 'diode', {}, at(BB, 'i21', 'diode', { rot: 270 })),
        part('bat', 'battery', { voltage: '9' }, { x: -112, z: 92, rot: 0 }),
        part('m1', 'dc-motor', { rotor: 'propeller' }, { x: 92, z: 70, rot: 0 }),
        part('m2', 'dc-motor', { rotor: 'propeller' }, { x: 158, z: 70, rot: 0 }),
        part('m3', 'dc-motor', { rotor: 'propeller' }, { x: 92, z: 132, rot: 0 }),
      ],
      wires: wires([
        ['uno:5V', 'bb1:bp2', 'red'],
        ['uno:GND.2', 'bb1:bn2', 'black'],
        ['bat:+', 'uno:VIN', 'red'],
        ['bat:-', 'bb1:bn4', 'black'],
        ['uno:9', 'bb1:j6', 'orange'],
        ['bb1:j12', 'bb1:bn10', 'black'],
        ['bb1:g11', 'bb1:g16', 'blue'],
        ['bb1:j16', 'bb1:j21', 'blue'],
        ['m1:+', 'bb1:bp20', 'red'],
        ['m1:-', 'bb1:h11', 'blue'],
        ['m2:+', 'bb1:bp22', 'red'],
        ['m2:-', 'bb1:h16', 'blue'],
        ['m3:+', 'bb1:bp24', 'red'],
        ['m3:-', 'bb1:h21', 'blue'],
      ]),
    },
  },
];
